import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ConfirmModal } from './ConfirmModal';
import { supabase } from '../lib/supabase';
import { useToast } from '../lib/useToast';
import { timeAgo } from '../lib/timeAgo';

/** A user whose posts (logs) the admin wants to inspect/moderate. */
export type UserPostsUser = {
  id: string;
  username: string | null;
  full_name: string | null;
  created_at: string;
  is_admin: boolean | null;
  avatar_url?: string | null;
};

type FlashMediaItem = { url?: string | null };

/** A "post" = a row in public.logs. Admins can SELECT all logs (logs_select_admin). */
type PostRow = {
  id: string;
  venue_id: string | null;
  venue_name: string | null;
  elo_score: number | null;
  atmosphere: number | null;
  music: number | null;
  drinks: number | null;
  service: number | null;
  lineup: number | null;
  venue_score: number | null;
  notes: string | null;
  mood_tag: string | null;
  flash_url: string | null;
  flash_media: FlashMediaItem[] | null;
  created_at: string;
  visit_date: string | null;
  is_public: boolean | null;
  is_event: boolean | null;
  deleted_at: string | null;
  retired_at: string | null;
};

const POST_SELECT =
  'id, venue_id, venue_name, elo_score, atmosphere, music, drinks, service, lineup, venue_score, notes, mood_tag, flash_url, flash_media, created_at, visit_date, is_public, is_event, deleted_at, retired_at';

const CRITERIA: { key: keyof PostRow; label: string }[] = [
  { key: 'atmosphere', label: 'Atmosphere' },
  { key: 'music', label: 'Music' },
  { key: 'drinks', label: 'Drinks' },
  { key: 'service', label: 'Service' },
  { key: 'lineup', label: 'Lineup' },
];

function firstPhoto(p: PostRow): string | null {
  const u = p.flash_url?.trim();
  if (u) return u;
  const media = Array.isArray(p.flash_media) ? p.flash_media : [];
  for (const item of media) {
    const url = item?.url?.trim();
    if (url) return url;
  }
  return null;
}

function rpcMissing(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('admin_hard_delete_log') ||
    m.includes('42883') ||
    m.includes('could not find the function')
  );
}

function Badge({ tone, children }: { tone: 'muted' | 'amber' | 'red' | 'violet'; children: ReactNode }) {
  const cls = {
    muted: 'border-border bg-white/[0.04] text-muted',
    amber: 'border-amber-500/40 bg-amber-500/15 text-amber-200',
    red: 'border-red-500/40 bg-red-500/15 text-red-200',
    violet: 'border-violet-500/40 bg-violet-500/15 text-violet-200',
  }[tone];
  return (
    <span className={`inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {children}
    </span>
  );
}

export function UserPostsModal({ user, onClose }: { user: UserPostsUser | null; onClose: () => void }) {
  const [posts, setPosts] = useState<PostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PostRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { showError, showSuccess, toastNode } = useToast();

  const load = useCallback(async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('logs')
        .select(POST_SELECT)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(300);
      if (e) throw e;
      setPosts((data ?? []) as PostRow[]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load posts');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.id) void load(user.id);
  }, [user?.id, load]);

  async function deletePost(post: PostRow) {
    setBusyId(post.id);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_hard_delete_log', { p_log_id: post.id });
      if (rpcErr) {
        if (rpcMissing(rpcErr.message)) {
          throw new Error('Delete is not enabled yet — apply migration 20260748000000 (supabase db push), then retry.');
        }
        throw rpcErr;
      }
      if (data && (data as { ok?: boolean }).ok === false) {
        throw new Error((data as { error?: string }).error ?? 'Delete failed');
      }
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      showSuccess('Post deleted');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusyId(null);
      setDeleteTarget(null);
    }
  }

  if (!user) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-8"
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <div
          className="w-full max-w-3xl rounded-xl border border-border bg-surface"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-border text-sm text-muted">
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  (user.username ?? '?').slice(0, 1).toUpperCase()
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="admin-label-primary text-base truncate">@{user.username ?? user.id.slice(0, 8)}</h2>
                  {user.is_admin ? (
                    <span className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-300">
                      Admin
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-mutedDim">
                  {user.full_name?.trim() ? `${user.full_name} · ` : ''}
                  Joined {new Date(user.created_at).toLocaleDateString()} · {posts.length} post
                  {posts.length === 1 ? '' : 's'}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-muted transition-colors hover:bg-white/[0.04] hover:text-white"
            >
              Close
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
            {loading ? (
              <p className="py-8 text-center text-sm text-muted">Loading posts…</p>
            ) : error ? (
              <div className="rounded-lg border border-red-500/35 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            ) : posts.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted">This user has no posts.</p>
            ) : (
              <ul className="space-y-3">
                {posts.map((p) => {
                  const photo = firstPhoto(p);
                  const busy = busyId === p.id;
                  return (
                    <li key={p.id} className="flex gap-4 rounded-xl border border-border bg-rowAlt p-3">
                      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-border">
                        {photo ? (
                          <img
                            src={photo}
                            alt=""
                            referrerPolicy="no-referrer"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-[10px] text-mutedDim">
                            no photo
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="admin-label-primary text-sm truncate">
                              {p.venue_name?.trim() || 'Untitled venue'}
                            </div>
                            <div className="text-xs text-mutedDim tabular-nums">
                              {p.visit_date ? new Date(p.visit_date).toLocaleDateString() : timeAgo(p.created_at)}
                              {typeof p.elo_score === 'number' ? ` · score ${Math.round(p.elo_score)}` : ''}
                            </div>
                          </div>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setDeleteTarget(p)}
                            className="shrink-0 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-500/20 disabled:opacity-50"
                          >
                            {busy ? 'Deleting…' : 'Delete'}
                          </button>
                        </div>

                        <div className="flex flex-wrap gap-1.5">
                          {p.is_public === false ? <Badge tone="muted">Private</Badge> : null}
                          {p.is_event ? <Badge tone="muted">Event</Badge> : null}
                          {p.retired_at ? <Badge tone="amber">Retired</Badge> : null}
                          {p.deleted_at ? <Badge tone="red">Hidden</Badge> : null}
                          {p.mood_tag?.trim() ? <Badge tone="violet">{p.mood_tag}</Badge> : null}
                        </div>

                        {p.notes?.trim() ? (
                          <p className="text-sm text-muted leading-snug line-clamp-3">{p.notes}</p>
                        ) : null}

                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-mutedDim tabular-nums">
                          {CRITERIA.map((c) => {
                            const v = p[c.key] as number | null;
                            return typeof v === 'number' ? (
                              <span key={String(c.key)}>
                                {c.label} {v}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        danger
        title="Delete this post?"
        message={
          deleteTarget
            ? `Permanently delete @${user.username ?? user.id}'s post for "${
                deleteTarget.venue_name?.trim() || 'this venue'
              }"? This removes it from the app and recomputes the venue's score. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete post"
        cancelLabel="Cancel"
        busy={Boolean(deleteTarget) && busyId === deleteTarget?.id}
        onConfirm={() => deleteTarget && void deletePost(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />

      {toastNode}
    </>
  );
}
